import lightThemeColors from '@prisjakt/design-tokens/light-theme-colors.json';
import { setRgbaAlpha } from '../../src/color-utils';

const theme = {
  colors: {
    ...lightThemeColors,
  },
  bg: {
    primary: lightThemeColors.base0,
    secondary: lightThemeColors.base100,
    overlay: setRgbaAlpha(lightThemeColors.base700, '0.2'), // darkBlue1 with 20% opacity,
    productCompare: lightThemeColors.yellove500,
    productCompareLight: setRgbaAlpha(lightThemeColors.yellove500, '0.05'),
  },
  header: {
    bg: lightThemeColors.maybeBlue500,
    fg: lightThemeColors.base100,
  },
  footer: {
    bg: {
      primary: lightThemeColors.base100,
      secondary: lightThemeColors.base100,
    },
    fg: {
      primary: lightThemeColors.base600,
    },
  },
  lines: {
    primary: lightThemeColors.base200,
    secondary: lightThemeColors.base300,
    tertiary: lightThemeColors.base500,
    quaternary: lightThemeColors.base700,
    highlight: lightThemeColors.maybeBlue700,
    groupHeader: lightThemeColors.yellove500,
  },
  text: {
    primary: lightThemeColors.base700,
    secondary: lightThemeColors.base600,
    tertiary: lightThemeColors.base500,
    quaternary: lightThemeColors.base400,

    linkExternal: lightThemeColors.maybeBlue700,
    linkExternalHover: lightThemeColors.maybeBlue500,
    linkInternal: lightThemeColors.base700,
    linkInternalHover: lightThemeColors.base700,
    callToAction: lightThemeColors.maybeBlue700,
    callToActionHover: lightThemeColors.maybeBlue500,
    negative: lightThemeColors.negative700,
    positive: lightThemeColors.positive500,
  },
  icons: {
    primary: lightThemeColors.base600,
    secondary: lightThemeColors.base500,
    tertiary: lightThemeColors.base400,
    quaternary: lightThemeColors.base300,
    light: lightThemeColors.base500,
    callToAction: lightThemeColors.maybeBlue700,
    callToActionHover: lightThemeColors.maybeBlue500,
    positive: lightThemeColors.positive500,
    negative: lightThemeColors.negative700,
  },
  buttons: {
    primary: {
      bg: lightThemeColors.maybeBlue700,
      bgActive: lightThemeColors.maybeBlue100,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    secondary: {
      bg: lightThemeColors.base500,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    tertiary: {
      bg: lightThemeColors.base200,
      text: lightThemeColors.base700,
      textHover: lightThemeColors.base700,
    },
    quarternary: {
      bg: lightThemeColors.base700,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    positive: {
      bg: lightThemeColors.positive700,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    negative: {
      bg: lightThemeColors.negative700,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    link: {
      bg: 'transparent',
      text: lightThemeColors.maybeBlue700,
      textHover: lightThemeColors.maybeBlue500,
    },
    disabled: {
      bg: lightThemeColors.base500,
      text: lightThemeColors.base0,
      textHover: lightThemeColors.base0,
    },
    category: {
      bg: lightThemeColors.base0,
      bgActive: setRgbaAlpha(lightThemeColors.maybeBlue700, '0.08'),
      text: lightThemeColors.base700,
      textHover: lightThemeColors.maybeBlue700,
    },
    panning: {
      bg: 'rgba(0, 0, 0, 0.6)',
      bgHover: 'rgba(0, 0, 0, 0.4)',
      text: lightThemeColors.base0,
    },
  },
  pill: {
    primary: {
      bg: lightThemeColors.maybeBlue700,
      text: lightThemeColors.base0,
    },
    inverted: {
      bg: lightThemeColors.base0,
      text: lightThemeColors.base700,
    },
  },
  inputs: {
    primary: {
      bg: lightThemeColors.base0,
      border: lightThemeColors.base500,
      borderFocus: lightThemeColors.maybeBlue700,
    },
  },
  dropdowns: {
    primary: {
      bg: lightThemeColors.base0,
      hoverBackground: setRgbaAlpha(lightThemeColors.maybeBlue700, '0.2'),
      borderRadius: '5px',
      borderColor: {
        active: lightThemeColors.base700,
        inactive: lightThemeColors.base700,
      },
    },
    secondary: {
      bg: lightThemeColors.base0,
      hoverBackground: setRgbaAlpha(lightThemeColors.maybeBlue700, '0.2'),
      borderRadius: 0,
      borderColor: {
        active: lightThemeColors.base200,
        inactive: 'transparent',
      },
    },
  },
  placeholders: {
    bg: lightThemeColors.base100,
    fg: lightThemeColors.base300,
  },
  cookieBanner: {
    bg: setRgbaAlpha(lightThemeColors.base700, '0.9'),
    fg: lightThemeColors.base0,
  },
  actionBar: {
    bg: lightThemeColors.base700,
    fg: lightThemeColors.base0,
    boxShadow: '0 -2px 10px 0 rgba(0, 0, 0, 0.2)',
  },
  ratings: {
    positive: {
      bg: lightThemeColors.positive300,
      fg: lightThemeColors.positive700,
    },
    neutral: {
      bg: lightThemeColors.notice300,
      fg: lightThemeColors.yellove900,
    },
    negative: {
      bg: lightThemeColors.negative300,
      fg: lightThemeColors.negative700,
    },
    disabled: { bg: lightThemeColors.base200, fg: lightThemeColors.base500 },
  },
  notifications: {
    info: {
      bg: setRgbaAlpha(lightThemeColors.maybeBlue700, '0.04'),
      fg: lightThemeColors.maybeBlue700,
    },
    issue: {
      bg: setRgbaAlpha(lightThemeColors.negative300, '0.2'),
      fg: setRgbaAlpha(lightThemeColors.negative700, '0.7'),
    },
    neutral: {
      bg: lightThemeColors.base100,
      fg: lightThemeColors.base600,
    },
  },
  promos: {
    bg: lightThemeColors.maybeBlue100,
    fg: lightThemeColors.base600,
  },
};

export type ThemeType = typeof theme & { layout?: string };

export default theme;
